import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import styles from './FeedbackPostDrawer.module.scss'

import RichTextEditor from '~common/RichTextEditor'
import { ProductIdeaPromoteToRoadmapCTA } from '~features/product-idea-promote-roadmap-cta'
import { DealFragment, FeedbackPostFragment, TagFragment } from '~generated/operations'
import { FeedbackPostStatus, TagTypes } from '~generated/types'
import { formatDate } from '~lib/dateUtils'
import { parseQueryParam } from '~lib/routes'
import Col from '~lib/ui-kit/Col'
import Label from '~lib/ui-kit/Label'
import NativeInput from '~lib/ui-kit/NativeInput'
import Row from '~lib/ui-kit/Row'
import DeleteFeedbackPostButton from '~modules/feedbackTool/containers/DeleteFeedbackPostButton'
import FeedbackPostComments from '~modules/feedbackTool/containers/FeedbackPostComments'
import FeedbackPostRevenueImpact from '~modules/feedbackTool/containers/FeedbackPostRevenueImpact'
import FeedbackPostVotesInput from '~modules/feedbackTool/containers/FeedbackPostVotesInput'
import FeedbackStatusSelect from '~modules/feedbackTool/elements/FeedbackStatusSelect'
import useTurnFeedbackPostToRoadmapItem from '~modules/feedbackTool/usecases/useTurnFeedbackPostToRoadmapItem'
import useUpdateFeedbackPost from '~modules/feedbackTool/usecases/useUpdateFeedbackPost'
import useUpdateFeedbackPostTags from '~modules/feedbackTool/usecases/useUpdateFeedbackPostTags'
import TagsSelect from '~modules/tags/containers/TagsSelect'
import useCreateTag from '~modules/tags/usecases/useCreateTag'
import { OrganizationNavigation } from '~shared/navigation'
import Divider from '~shared/ui/Divider'

interface FeedbackPostDrawerContentProps {
  feedbackPost: FeedbackPostFragment
  readOnly?: boolean
  disabled?: boolean
  tags?: TagFragment[]
}

function FeedbackPostDrawerContent({ feedbackPost, tags, readOnly, disabled }: FeedbackPostDrawerContentProps) {
  const router = useRouter()
  const organizationSlug = parseQueryParam(router.query, 'organizationSlug')

  const [updateFeedbackPost, { data }] = useUpdateFeedbackPost(feedbackPost)
  const [turnFeedbackPostToRoadmapItem, { loading: promotionLoading }] = useTurnFeedbackPostToRoadmapItem({
    onCompleted: ({ turnFeedbackPostIntoProductBoardItem }) => {
      router.push({
        pathname: OrganizationNavigation.organization.roadmap.roadmapItem.route.pathname,
        query: { organizationSlug, roadmapItemId: turnFeedbackPostIntoProductBoardItem.id, firstVisit: true },
      })
    },
  })

  const [createTag] = useCreateTag()

  const [feedbackPostTags, updateFeedbackPostTags] = useUpdateFeedbackPostTags({
    id: feedbackPost?.id,
    tags: feedbackPost?.tags,
    updatedAt: feedbackPost?.updatedAt,
  })

  const tagIds = useMemo(() => {
    return feedbackPostTags.map((tag) => tag.id)
  }, [feedbackPostTags])

  const handleConvertToRoadmapItem = (roadmapId: string) => {
    turnFeedbackPostToRoadmapItem(roadmapId, data?.id)
  }

  const handleUpdateTitle = (ev: React.ChangeEvent<HTMLInputElement>) => {
    updateFeedbackPost({ title: ev.currentTarget.value })
  }

  const handleUpdateDescription = (description: string) => {
    updateFeedbackPost({ description })
  }

  const handleUpdateStatus = (status: FeedbackPostStatus) => {
    updateFeedbackPost({ status })
  }

  const handleUpdateDeals = useCallback(
    (deals: DealFragment[]) => {
      updateFeedbackPost({ deals })
    },
    [updateFeedbackPost]
  )

  return (
    <>
      <Row align="start" wrap={false} className="mb-3">
        <Col className="py-1 mr-1">
          <FeedbackPostVotesInput
            feedbackPostId={data.id}
            upvotes={data.upvotes}
            upvoters={data.upvoters}
            upvotedByUser={data.upvotedByUser}
            readOnly={readOnly}
            disabled={disabled}
          />
        </Col>

        <Col flex={1}>
          <Row align="center" justify="space-between">
            <NativeInput
              size="body-l"
              placeholder="Product idea name"
              bold
              value={data?.title}
              onChange={handleUpdateTitle}
              readOnly={readOnly}
              disabled={disabled}
            />
          </Row>

          <Row align="center">
            <FeedbackStatusSelect
              value={data?.status}
              onChange={handleUpdateStatus}
              readOnly={readOnly}
              disabled={disabled}
              className={styles.status}
            />

            <TagsSelect
              value={tagIds}
              tags={tags}
              displayTags
              label="Tags"
              type={TagTypes.FEEDBACK_POST}
              readOnly={readOnly}
              size="sm"
              onChange={updateFeedbackPostTags}
              onCreate={createTag}
              className={styles.tags}
            />

            {feedbackPost?.createdAt && (
              <Label bordered size="sm" variant="dark">
                {formatDate(new Date(feedbackPost.createdAt), 'day2DigitsMonthShortYearNumeric')}
              </Label>
            )}
          </Row>
        </Col>

        {!readOnly && (
          <Col className="py-1 ml-1">
            <DeleteFeedbackPostButton feedbackPostId={data?.id} />
          </Col>
        )}
      </Row>

      <RichTextEditor
        placeholder="Description"
        initialContent={data?.description}
        onChange={handleUpdateDescription}
        mediaEnabled
        readOnly={readOnly}
        className={styles.description}
      />

      {!readOnly && (
        <ProductIdeaPromoteToRoadmapCTA onRoadmapClick={handleConvertToRoadmapItem} loading={promotionLoading} />
      )}

      <Divider className="my-4" />

      <FeedbackPostRevenueImpact
        deals={data?.deals}
        onDealsChange={handleUpdateDeals}
        className="mb-2"
        readOnly={readOnly}
      />
      {!readOnly && <FeedbackPostComments fedbackPostId={data?.id} />}
    </>
  )
}

export default FeedbackPostDrawerContent
